/**
 * Taalcodes zijn taal-regio (`nl-NL`, `en-GB`, …), en teksten worden gevonden via één keten:
 * exacte code → hoofdvorm van de taal → standaardtaal. Een variant (`nl-BE`) bevat alleen wat
 * afwijkt. Spec `docs/superpowers/specs/2026-09-26-taalcodes-taal-regio-design.md`.
 */
import { z } from 'zod'

export const LOCALES = ['en-GB', 'nl-NL', 'de-DE', 'fr-FR'] as const
export type Locale = (typeof LOCALES)[number]
export const DEFAULT_LOCALE: Locale = 'en-GB'

/** De hoofdvorm per taal: waar een variant op terugvalt. */
export const MAIN_LOCALE: Record<string, Locale> = {
  en: 'en-GB',
  nl: 'nl-NL',
  de: 'de-DE',
  fr: 'fr-FR',
}

/** De taal van een code (`nl` uit `nl-BE`), voor de taalkolommen per taal. Ongeldig → `''`. */
export function languageOf(code: string): string {
  try {
    return new Intl.Locale(code).language
  } catch {
    return ''
  }
}

/** Een oude of onvolledige code (`nl`, `NL`, `nl_NL`) naar de vorm van nu. Ongeldig → `null`. */
export function normalizeLocale(code: string | null | undefined): string | null {
  const raw = (code ?? '').trim().replaceAll('_', '-')
  if (!raw) return null
  let canonical: string
  try {
    canonical = Intl.getCanonicalLocales(raw)[0]!
  } catch {
    return null
  }
  // Een kale taal (`nl`) is de oude vorm: die betekent de hoofdvorm van die taal.
  return canonical.includes('-') ? canonical : (MAIN_LOCALE[canonical] ?? canonical)
}

/**
 * De eerste kandidaat die een echte taal-regiocode is (`isLocaleRegion`), genormaliseerd; anders
 * `null`. Voor een keten als "taal van de order, anders die van de klant": `a ?? b` kiest ook een
 * LEGE of onzinnige code (`''`, `Deutsch`), en sloeg dan de klant over. Staat `isLocaleRegion`
 * verderop — een functiedeclaratie, dus hier al bruikbaar.
 */
export function firstLocale(...candidates: Array<string | null | undefined>): string | null {
  for (const c of candidates) {
    if (isLocaleRegion(c)) return normalizeLocale(c)
  }
  return null
}

/**
 * De taal van een document: die van het record, en ALLEEN als die leeg is de standaardtaal van
 * de tenant. De tenanttaal zit dus niet in de keten van een record met taal: een Duitse klant
 * zonder Duitse tekst krijgt Engels, niet de taal van de tenant (spec §3.3). Een lege of
 * onzinnige recordtaal telt als leeg (`firstLocale`).
 */
export function documentLocale(
  recordCode: string | null | undefined,
  tenantCode: string | null | undefined,
): string {
  return firstLocale(recordCode) ?? firstLocale(tenantCode) ?? DEFAULT_LOCALE
}

/**
 * De taal van een gebruiker (spec §3.5): eigen taal → een keuze die al in deze browser stond →
 * taal van de gekoppelde medewerker → standaardtaal van de tenant → browsertaal → app-standaard.
 * `adoptStored`: de browserkeuze won, en hoort nu de eigen taal te worden — zo houdt wie vóór
 * de eigen taal al koos zijn keuze, en overschrijft een oude keuze op een tweede apparaat
 * nooit een nieuwere.
 */
export function userLocaleOrder(o: {
  own?: string | null
  stored?: string | null
  employee?: string | null
  tenant?: string | null
  browser?: string | null
}): { locale: string; adoptStored: boolean } {
  const order = [o.own, o.stored, o.employee, o.tenant, o.browser]
  for (let i = 0; i < order.length; i++) {
    const code = normalizeLocale(order[i])
    if (code) return { locale: code, adoptStored: i === 1 }
  }
  return { locale: DEFAULT_LOCALE, adoptStored: false }
}

/** De keten: exact → hoofdvorm van de taal → standaardtaal. */
export function localeChain(code: string | null | undefined): string[] {
  const out: string[] = []
  const add = (c: string | null | undefined) => {
    if (c && !out.includes(c)) out.push(c)
  }
  const n = normalizeLocale(code)
  add(n)
  if (n) add(MAIN_LOCALE[languageOf(n)])
  add(DEFAULT_LOCALE)
  return out
}

/** De eerste waarde langs de keten; een lege string telt niet als waarde. */
export function pickLocalized<T>(
  map: Record<string, T> | null | undefined,
  code: string | null | undefined,
): T | undefined {
  if (!map) return undefined
  for (const c of localeChain(code)) {
    const v = map[c]
    if (v === undefined || v === null) continue
    if (typeof v === 'string' && v.trim() === '') continue
    return v
  }
  return undefined
}

/** Een tekst uit een taalmap volgens de keten (leeg als geen enkele sleutel iets heeft). */
export function pickText(
  map: Record<string, string> | null | undefined,
  code: string | null | undefined,
): string {
  return pickLocalized(map, code) ?? ''
}

/**
 * Een labelmap (`{ nl: …, en: … }`) naar taal-regio-sleutels, zoals hij wordt opgeslagen.
 * Lege waarden en ongeldige sleutels vallen weg; staan oud en nieuw er allebei, dan wint de
 * nieuwe sleutel, tenzij die leeg is — dezelfde regel als de datamigratie.
 */
export function normalizeLocaleKeys(map: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  const exact = new Set<string>()
  for (const [key, raw] of Object.entries(map)) {
    const code = normalizeLocale(key)
    if (!code || typeof raw !== 'string' || !raw.trim()) continue
    const isExact = key === code
    if (code in out && exact.has(code) && !isExact) continue
    out[code] = raw
    if (isExact) exact.add(code)
  }
  return out
}

const languageNames = (() => {
  try {
    return new Intl.DisplayNames(DEFAULT_LOCALE, { type: 'language', fallback: 'none' })
  } catch {
    return null
  }
})()
const regionNames = (() => {
  try {
    return new Intl.DisplayNames(DEFAULT_LOCALE, { type: 'region', fallback: 'none' })
  } catch {
    return null
  }
})()

/**
 * Is dit, na `normalizeLocale`, een echte taal-regiocode: een BEKENDE taal mét een BEKENDE regio?
 * `normalizeLocale` toetst alleen de BCP-47-syntaxis, en daar komen `deutsch`, `abc` en een kale
 * `es` gewoon doorheen. Opgeslagen kortsloot zo'n code de terugval naar de tenanttaal
 * (`documentLocale` ziet hem als recordtaal), en leverde dan Engels op. Spec §2.1: elke taalcode
 * is taal-regio, geen uitzonderingen.
 */
export function isLocaleRegion(code: string | null | undefined): boolean {
  const n = normalizeLocale(code)
  if (!n) return false
  let loc: Intl.Locale
  try {
    loc = new Intl.Locale(n)
  } catch {
    return false
  }
  if (!loc.region || loc.language === 'und') return false
  // Zonder `Intl.DisplayNames` (een oude runtime) alleen de talen die de app zelf kent.
  if (!languageNames || !regionNames) return loc.language in MAIN_LOCALE
  return languageNames.of(loc.language) !== undefined && regionNames.of(loc.region) !== undefined
}

/**
 * Een optioneel taalveld in een invoerschema. Een ONTBREKENDE sleutel blijft `undefined`: een
 * update die het veld niet meestuurt mag een opgeslagen taal niet wissen (`nullish().transform`
 * maakte er `null` van, en de routers spreiden de invoer in `.set()`). `null`/leeg → `null`; een
 * niet-lege code die geen taal-regio is (`isLocaleRegion`), is een validatiefout in plaats van een stille `null`.
 */
export const localeInput = z
  .string()
  .max(35)
  .nullish()
  .refine((v) => v == null || v.trim() === '' || isLocaleRegion(v), 'Ongeldige taalcode')
  .transform((v) => (v === undefined ? undefined : normalizeLocale(v)))

/** Een verplicht taalveld, genormaliseerd. */
export const localeCode = z
  .string()
  .min(2)
  .max(35)
  .refine((v) => isLocaleRegion(v), 'Ongeldige taalcode')
  .transform((v) => normalizeLocale(v)!)
